import type {
  LabeledBox,
  LabelerPrediction,
  NormalizedBox,
  PageLabels,
} from './types'
import type { WorkingCopy } from './workingCopy'

/**
 * Inversion of the published per-user manual list back into a working copy —
 * the "live view" model: one list per (site, user), the working copy is just
 * a local materialization of it, hydrated when a machine has no local state
 * and synced back on every edit.
 *
 * Lossy corners of the round trip, accepted: per-box verification comes back
 * as the record's aggregate (confidence 1.0 = all verified), and seed
 * provenance (seedRunId / predictionId) does not survive — hydrated boxes are
 * origin 'drawn', so re-seeding after hydration will re-add the extractor's
 * predictions alongside them rather than replacing them.
 */

interface PublishedReference {
  type: string
  ref: string
  document_id?: string | null
  page?: number | null
  is_citation?: boolean | null
  boxes?: string[] | null
}

interface PublishedAlias {
  value: string
  norm: string
  kind: string
  precision: string
  references: PublishedReference[]
}

export interface PublishedRecord {
  id: string
  data: {
    aliases: PublishedAlias[]
    type_guess?: string | null
    kind_guess?: string | null
    confidence?: number
  }
}

const newBoxId = () => `box-${Math.random().toString(36).slice(2, 10)}`

export function parseBoxString(value: string): NormalizedBox | null {
  const match =
    /^bbox:([\d.eE+-]+),([\d.eE+-]+),([\d.eE+-]+),([\d.eE+-]+)$/.exec(
      value.trim()
    )
  if (!match) return null
  const [x0, y0, x1, y1] = match.slice(1).map(Number)
  if ([x0, y0, x1, y1].some((n) => !Number.isFinite(n))) return null
  return {
    x_min: Math.min(x0, x1),
    y_min: Math.min(y0, y1),
    x_max: Math.max(x0, x1),
    y_max: Math.max(y0, y1),
  }
}

/**
 * A source list's page slice, in prediction shape — the operating unit for
 * seeding and comparison is the source list (what fusion actually consumes),
 * so a list's records project onto one page exactly like an extractor run's
 * rows do. `dagster_run_id` carries the list id so seeds and verdicts record
 * which source they were judged against.
 */
export function recordsToPageSlice(
  records: PublishedRecord[],
  pageId: string,
  sourceListId: string
): LabelerPrediction[] {
  const slice: LabelerPrediction[] = []
  for (const record of records) {
    const tagAlias = record.data.aliases.find((a) => a.kind === 'canonical_tag')
    if (!tagAlias) continue
    const name =
      record.data.aliases.find((a) => a.kind === 'description')?.value ?? null
    for (const ref of tagAlias.references) {
      if (ref.type !== 'doc_page' || ref.ref !== pageId || !ref.boxes?.length)
        continue
      ref.boxes.forEach((boxString, index) => {
        const bbox = parseBoxString(boxString)
        if (!bbox) return
        slice.push({
          id: `${record.id}#${index}`,
          text: tagAlias.value,
          name,
          type: record.data.type_guess ?? null,
          kind: record.data.kind_guess ?? null,
          vlm_type: null,
          regex_type: null,
          arbitration_reason: null,
          page_category: null,
          is_reference: ref.is_citation ?? false,
          x0: bbox.x_min,
          y0: bbox.y_min,
          x1: bbox.x_max,
          y1: bbox.y_max,
          dagster_run_id: sourceListId,
          classify_tags_code_version: null,
          extract_ocr_tag_directed_code_version: null,
        })
      })
    }
  }
  slice.sort((a, b) => a.y0 - b.y0 || a.x0 - b.x0)
  return slice
}

export function recordsToWorkingCopy(
  records: PublishedRecord[],
  siteId: string,
  labeledPages: string[]
): WorkingCopy {
  const pages: WorkingCopy = {}
  const completeSet = new Set(labeledPages)
  const now = new Date().toISOString()

  for (const record of records) {
    const tagAlias = record.data.aliases.find((a) => a.kind === 'canonical_tag')
    if (!tagAlias) continue
    const name =
      record.data.aliases.find((a) => a.kind === 'description')?.value ?? null
    const verified = (record.data.confidence ?? 0) >= 1

    for (const ref of tagAlias.references) {
      if (ref.type !== 'doc_page' || !ref.boxes?.length) continue
      const pageId = ref.ref
      const documentId = ref.document_id ?? pageId.replace(/_pg_\d+$/, '')
      const pageNum = ref.page ?? Number(/_pg_(\d+)$/.exec(pageId)?.[1] ?? 1)

      let page: PageLabels | undefined = pages[pageId]
      if (!page) {
        page = {
          pageId,
          siteId,
          documentId,
          pageNum,
          boxes: [],
          rejections: [],
          seedRunId: null,
          complete: completeSet.has(pageId),
          updatedAt: now,
        }
        pages[pageId] = page
      }

      for (const boxString of ref.boxes) {
        const bbox = parseBoxString(boxString)
        if (!bbox) continue
        const box: LabeledBox = {
          id: newBoxId(),
          bbox,
          text: tagAlias.value,
          name,
          type: record.data.type_guess ?? null,
          isReference: ref.is_citation ?? false,
          verified,
          origin: 'drawn',
          predictionId: null,
        }
        page.boxes.push(box)
      }
    }
  }
  return pages
}
